/**
 * A ticket is built once, as data, and rendered by two backends: the Sunmi
 * thermal printer on Android and a canvas in the browser. Both consume this
 * same document, which is the only reason the preview and the paper can be
 * trusted to match.
 *
 * Nothing here knows about ESC/POS or about canvas. Renderers live in their
 * own packages.
 */
import { formatCentsPlain } from './money.js';
/** Wrap a string to the ticket width without breaking mid-word when avoidable. */
export function wrapText(value, columns) {
    const words = value.split(/\s+/).filter(Boolean);
    if (words.length === 0)
        return [''];
    const lines = [];
    let line = '';
    for (const word of words) {
        if (word.length > columns) {
            if (line) {
                lines.push(line);
                line = '';
            }
            for (let i = 0; i < word.length; i += columns) {
                lines.push(word.slice(i, i + columns));
            }
            continue;
        }
        const candidate = line ? `${line} ${word}` : word;
        if (candidate.length > columns) {
            lines.push(line);
            line = word;
        }
        else {
            line = candidate;
        }
    }
    if (line)
        lines.push(line);
    return lines;
}
/**
 * Render a two-column row into a fixed-width line. The right side is never
 * truncated, because that side is money. The left side gives way instead.
 */
export function renderRow(left, right, columns) {
    const r = right.trim();
    const available = Math.max(0, columns - r.length - 1);
    const l = left.length > available ? `${left.slice(0, Math.max(0, available - 1))}…` : left;
    const gap = Math.max(1, columns - l.length - r.length);
    return `${l}${' '.repeat(gap)}${r}`;
}
export function alignText(value, columns, align) {
    if (value.length >= columns)
        return value;
    if (align === 'right')
        return value.padStart(columns);
    if (align === 'center') {
        const left = Math.floor((columns - value.length) / 2);
        return `${' '.repeat(left)}${value}`;
    }
    return value;
}
function formatQty(qty) {
    return Number.isInteger(qty) ? String(qty) : qty.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
}
/**
 * Deliberately small and explicit (found in integrator review): a value
 * that passes the shared schema's shape check (letters only, 2-20 chars —
 * see domain.ts's own comment) is NOT the same claim as "safe to print on
 * a customer's receipt". Mercado Pago's own payment_method.id vocabulary
 * includes compound internal codes like "debvisa" that are syntactically
 * fine but meaningless to a customer. Only `cardBrand` values actually
 * confirmed (live, for "master") or clearly recognizable network names are
 * allowlisted; anything else is dropped from the printed line, falling
 * back to printing just the last 4 digits with no brand — never guessed.
 */
const KNOWN_CARD_BRANDS = new Set(['visa', 'master', 'mastercard', 'amex']);
function isKnownCardBrand(brand) {
    return KNOWN_CARD_BRANDS.has(brand.toLowerCase());
}
export function buildTicket(input) {
    const { columns, labels } = input;
    const nodes = [];
    // Header lines print ABOVE the business name — the client wants the web
    // address as the literal first line of every ticket (2026-07-31).
    for (const line of input.header) {
        nodes.push({ type: 'text', value: line, align: 'center' });
    }
    nodes.push({ type: 'text', value: input.businessName, align: 'center', size: 'large', bold: true });
    nodes.push({ type: 'feed', lines: 1 });
    // A reprint declares itself loudly. A reprinted ticket must never be
    // mistakable for an original.
    if (input.isReprint) {
        nodes.push({ type: 'rule', char: '*' });
        nodes.push({
            type: 'text',
            value: `${labels.reprint} — ${labels.copy} ${input.copyNo}`,
            align: 'center',
            bold: true,
        });
        nodes.push({ type: 'rule', char: '*' });
    }
    if (input.voided) {
        nodes.push({ type: 'text', value: labels.voided, align: 'center', size: 'large', bold: true });
        nodes.push({ type: 'rule', char: '*' });
    }
    nodes.push({ type: 'row', left: labels.folio, right: input.folio });
    nodes.push({
        type: 'row',
        left: labels.date,
        right: input.soldAt.toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' }),
    });
    nodes.push({ type: 'row', left: labels.attendedBy, right: input.staffName });
    nodes.push({ type: 'row', left: labels.terminal, right: input.terminalName });
    nodes.push({ type: 'rule' });
    let itemCount = 0;
    for (const line of input.lines) {
        itemCount += line.qty;
        for (const chunk of wrapText(line.name, columns)) {
            nodes.push({ type: 'text', value: chunk });
        }
        nodes.push({
            type: 'row',
            left: `  ${formatQty(line.qty)} x ${formatCentsPlain(line.unitPriceCents)}`,
            right: formatCentsPlain(line.totalCents + line.discountCents),
        });
        if (line.discountCents > 0) {
            nodes.push({
                type: 'row',
                left: `  ${labels.discount}`,
                right: `-${formatCentsPlain(line.discountCents)}`,
            });
            // The reason gets its own wrapped lines rather than sharing the row with
            // the amount. On 32 columns a shared row truncates the reason to
            // something like "Cliente frec…", and a discount whose justification is
            // unreadable on the ticket defeats the purpose of requiring one.
            if (line.discountReason) {
                for (const chunk of wrapText(line.discountReason, columns - 4)) {
                    nodes.push({ type: 'text', value: `    ${chunk}` });
                }
            }
        }
    }
    nodes.push({ type: 'rule' });
    nodes.push({ type: 'row', left: labels.items, right: formatQty(itemCount) });
    nodes.push({ type: 'row', left: labels.subtotal, right: formatCentsPlain(input.subtotalCents) });
    if (input.discountCents > 0) {
        nodes.push({
            type: 'row',
            left: labels.discount,
            right: `-${formatCentsPlain(input.discountCents)}`,
        });
    }
    nodes.push({
        type: 'row',
        left: labels.total,
        right: formatCentsPlain(input.totalCents),
        bold: true,
        size: 'large',
    });
    nodes.push({ type: 'rule' });
    for (const p of input.payments) {
        if (p.method === 'cash') {
            nodes.push({ type: 'row', left: labels.cash, right: formatCentsPlain(p.amountCents) });
            if (p.tenderedCents != null) {
                nodes.push({ type: 'row', left: labels.tendered, right: formatCentsPlain(p.tenderedCents) });
            }
            if (p.changeCents != null && p.changeCents > 0) {
                nodes.push({
                    type: 'row',
                    left: labels.change,
                    right: formatCentsPlain(p.changeCents),
                    bold: true,
                });
            }
        }
        else {
            const kind = p.cardType === 'credit' ? labels.credit : labels.debit;
            nodes.push({
                type: 'row',
                left: `${labels.card} ${kind}`,
                right: formatCentsPlain(p.amountCents),
            });
            if (p.authNumber) {
                nodes.push({ type: 'row', left: `  ${labels.auth}`, right: p.authNumber });
            }
            if ((p.cardBrand && isKnownCardBrand(p.cardBrand)) || p.cardLast4) {
                const brand = p.cardBrand && isKnownCardBrand(p.cardBrand) ? p.cardBrand.toUpperCase() : null;
                const right = [brand, p.cardLast4 ? `**** ${p.cardLast4}` : null].filter((part) => Boolean(part)).join(' ');
                nodes.push({ type: 'row', left: `  ${labels.card}`, right });
            }
        }
    }
    nodes.push({ type: 'feed', lines: 1 });
    for (const line of input.footer) {
        nodes.push({ type: 'text', value: line, align: 'center' });
    }
    // The QR is the literal last printed element (client, 2026-08-02): after
    // every footer line, before the tear-off feed. Renderers center it.
    const qrUrl = input.qrUrl?.trim();
    if (qrUrl) {
        nodes.push({ type: 'feed', lines: 1 });
        nodes.push({ type: 'qr', value: qrUrl });
    }
    nodes.push({ type: 'feed', lines: 3 });
    nodes.push({ type: 'cut' });
    return { columns, nodes };
}
/** Flatten to plain text. Used by the canvas renderer and by tests. */
export function renderTicketToLines(doc) {
    const out = [];
    for (const node of doc.nodes) {
        switch (node.type) {
            case 'text': {
                const width = node.size === 'normal' || !node.size ? doc.columns : Math.floor(doc.columns / 2);
                for (const chunk of wrapText(node.value, width)) {
                    out.push(alignText(chunk, width, node.align ?? 'left'));
                }
                break;
            }
            case 'row':
                out.push(renderRow(node.left, node.right, doc.columns));
                break;
            case 'rule':
                out.push((node.char ?? '-').repeat(doc.columns));
                break;
            case 'feed':
                for (let i = 0; i < node.lines; i += 1)
                    out.push('');
                break;
            case 'qr':
                out.push(`[QR ${node.value}]`);
                break;
            case 'cut':
                break;
        }
    }
    return out;
}
/**
 * The slip for the kitchen (Alfredo, 2026-10-18): a «COMANDA» title, the date
 * and time, then each item with its flavours. Nothing else: no prices, no folio,
 * no business name, no staff. A REPRINT also says so under the title, because
 * a duplicate order must never be mistaken for a new one (the kitchen would
 * make it twice). A separate document from the customer's receipt because the
 * printer has no cutter: the cashier tears the receipt off first, then sends
 * this one.
 */
export function buildKitchenTicket(input) {
    const { columns } = input;
    const nodes = [];
    nodes.push({ type: 'text', value: input.title, align: 'center', size: 'large', bold: true });
    if (input.reprintLabel) {
        nodes.push({ type: 'text', value: input.reprintLabel, align: 'center', bold: true });
    }
    const customer = input.customerName?.trim();
    if (customer) {
        for (const chunk of wrapText(customer, Math.max(8, Math.floor(columns / 2)))) {
            nodes.push({ type: 'text', value: chunk, align: 'center', size: 'large', bold: true });
        }
    }
    nodes.push({
        type: 'text',
        value: input.soldAt.toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' }),
        align: 'center',
        bold: true,
    });
    nodes.push({ type: 'feed', lines: 1 });
    for (const line of input.lines) {
        for (const chunk of wrapText(`${formatQty(line.qty)} x ${line.name}`, columns)) {
            nodes.push({ type: 'text', value: chunk, bold: true });
        }
        const flavours = (line.options ?? []).join(', ');
        if (flavours) {
            for (const chunk of wrapText(flavours, columns - 2)) {
                nodes.push({ type: 'text', value: `  ${chunk}` });
            }
        }
        const extras = (line.extras ?? []).join(', ');
        if (extras) {
            for (const chunk of wrapText(`+ ${extras}`, columns - 2)) {
                nodes.push({ type: 'text', value: `  ${chunk}` });
            }
        }
        nodes.push({ type: 'feed', lines: 1 });
    }
    nodes.push({ type: 'feed', lines: 3 });
    nodes.push({ type: 'cut' });
    return { columns, nodes };
}
//# sourceMappingURL=ticket.js.map